import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import ScriptCoPhysicianOpportunity from "./ScriptCoPhysicianOpportunity";
import ScriptCoPatientDetails from "./ScriptCoPatientDetails";

const ScriptCoOrderDetailsForm = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
}) => {
  const [validation, setValidation] = useState({
    physician: true,
    patientDetails: false,
  });

  const setPhysicianValid = useCallback((isValid) => {
    setValidation((prev) => {
      if (prev.physician === isValid) return prev;
      return { ...prev, physician: isValid };
    });
  }, []);

  const setPatientDetailsValid = useCallback((isValid) => {
    setValidation((prev) => {
      if (prev.patientDetails === isValid) return prev;
      return { ...prev, patientDetails: isValid };
    });
  }, []);

  useEffect(() => {
    if (onValidationChange) {
      const allValid = Object.values(validation).every((v) => v === true);
      onValidationChange(allValid);
    }
  }, [validation, onValidationChange]);

  const handleScriptCoDetailsChange = useCallback(
    (field, value) => {
      onDataChange("scriptCoDetails", {
        ...data?.scriptCoDetails,
        [field]: value,
      });
      if (["allergies", "conditions", "medications"].includes(field)) {
        onDataChange("medical", {
          ...data?.medical,
          [field]: value,
        });
      }
    },
    [onDataChange, data.scriptCoDetails, data.medical],
  );

  const hasOrderId = Boolean(
    opportunity?.vendorReferenceId ||
    opportunity?.vendorPreferenceId ||
    opportunity?.orderId ||
    opportunity?.scriptCoOrderDetails?.orderNumber ||
    opportunity?.scriptCoOrderDetails?.id ||
    opportunity?.scriptCoOrderDetails?.orderId ||
    opportunity?.grmOrderDetails?.orderNumber ||
    opportunity?.grmOrderDetails?.id ||
    data?.physician?.orderId ||
    data?.preferences?.orderId,
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
      }}
    >
      {/* Row 1: ScriptCo Patient & Insurance Details + Medical Fields (Full Width) */}
      <ScriptCoPatientDetails
        opportunity={opportunity}
        data={{
          ...data?.medical,
          ...data?.scriptCoDetails,
        }}
        onChange={handleScriptCoDetailsChange}
        onValidationChange={setPatientDetailsValid}
        showErrors={showErrors}
      />

      {/* Row 2: Prescribing Physician Details (Full Width) */}
      <ScriptCoPhysicianOpportunity
        opportunity={opportunity}
        data={data.physician || {}}
        onDataChange={useCallback(
          (newData) => onDataChange("physician", newData),
          [onDataChange],
        )}
        onValidationChange={setPhysicianValid}
        showErrors={showErrors}
        disabled={hasOrderId}
      />
    </Box>
  );
};

ScriptCoOrderDetailsForm.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default ScriptCoOrderDetailsForm;
